/**
 * List formatting for the chat input textarea.
 *
 * The composer is a plain `<textarea>`, so there is no way to render a real
 * `<ol>`/`<ul>`. The only feedback channels are the text content itself and the
 * caret. So list mode is made visible the only way a textarea allows: by writing
 * literal indentation and a real bullet glyph into the value.
 *
 * Two representations therefore exist, and keeping them straight is the whole
 * design:
 *
 *   - **Composer form** — what the user sees and edits. List lines carry one
 *     cosmetic `LIST_INDENT` level so they sit visibly right of surrounding
 *     prose, and bullets use `•` so they look like bullets.
 *   - **Markdown form** — what ships in the message payload. `toMarkdownList`
 *     strips exactly one indent level and rewrites `•` back to `-`, so the sent
 *     text is clean CommonMark that renders as a real list.
 *
 * Pure helpers so the whole feature is unit-testable without rendering the
 * textarea. One entry point per key the composer reacts to:
 *
 * `listActivationEdit` — SPACE. Turns a bare marker at the end of a line into a
 * list line, indenting it and normalising the bullet glyph, so activation is
 * unmistakable:
 *
 *   - `1.` + space → `␣␣␣1. ` (ordered). The dot is required; a bare digit does
 *     not activate, so prose like `1 million reasons` is left alone.
 *   - `*`, `-` or `•` + space → `␣␣␣• ` (unordered).
 *   - Anything else — a mid-line caret, an active selection, a marker with text
 *     already after it — returns null and the space is left to the browser.
 *
 * `listIndentEdit` — TAB / SHIFT+TAB. Nests or un-nests the list line the caret
 * sits on by one `LIST_INDENT` level, renumbering ordered markers for their new
 * level. Outdent stops at the base level so a list item never lands in column 0.
 * Returns null off a list line, leaving Tab its normal focus behaviour.
 *
 * `nextListEdit` — ENTER. Given the current value and caret, decides what should
 * happen on a list line:
 *
 *   - On a non-empty list line → insert a newline prefilled with the next marker,
 *     preserving the line's indent level.
 *   - On an empty *nested* item → outdent one level, so Enter can walk back out
 *     of a nested list.
 *   - On an empty item at the base level → remove the marker so the list
 *     terminates instead of opening another item.
 *   - Anything else (non-list line, an active selection) → returns null,
 *     signalling the caller to leave the keypress untouched.
 */

export interface ListEditResult {
  /** The new textarea value to apply. */
  value: string;
  /** Where the caret should sit after the value is applied. */
  caret: number;
}

export type ListType = "ordered" | "unordered";

export interface ListActivationResult extends ListEditResult {
  /** Which kind of list was activated — reported so the caller can track it. */
  listType: ListType;
}

export type IndentDirection = "indent" | "outdent";

export interface ListIndentResult extends ListEditResult {
  listType: ListType;
  direction: IndentDirection;
}

/**
 * One nesting level, and also the cosmetic indent every list line carries.
 *
 * Three spaces because that is the content column of an ordered marker (`1. `),
 * so after `toMarkdownList` drops the cosmetic level a nested line still sits at
 * or past its parent's content column under both `1. ` (3) and `- ` (2) — which
 * is what CommonMark requires to read it as a nested list rather than a sibling.
 */
export const LIST_INDENT = "   ";

/** The bullet the user sees in the composer. Not valid markdown on its own. */
export const BULLET_GLYPH = "•";

/** The bullet that ships in the payload. */
export const MARKDOWN_BULLET = "-";

// --- Enter/Tab-time markers (an item may already have text after the marker) ---

// Leading indent, the list number, then either a dot (with optional space) or
// just a space. Matches `1. text`, `1.text`, and `1 text`.
// `1)`-style markers intentionally do not match.
const NUMBERED_LINE = /^(\s*)(\d+)(?:\.\s?|\s)/;

// Leading indent then a bullet character followed by whitespace. The whitespace
// is required so `-5 degrees` and `*emphasis*` are not read as bullet lines.
const BULLET_LINE = /^(\s*)([*\-•])\s/;

// --- Space-time markers (the marker must be the whole line so far) ---

const ORDERED_ACTIVATION = /^(\s*)(\d+)\.$/;
const BULLET_ACTIVATION = /^(\s*)([*\-•])$/;

// --- Submit-time marker (indent, marker, and the space after it) ---

const MARKDOWN_LIST_LINE = /^(\s*)([*\-•]|\d+\.)(\s)/;

interface MarkerInfo {
  /** The raw marker text as the user wrote it, including indent and trailing space. */
  marker: string;
  /** Just the leading whitespace of the line. */
  indent: string;
  /** The canonical form of this line's marker, indent included. */
  canonical: string;
  /** The marker that opens the following item (no leading newline). */
  nextMarker: string;
  listType: ListType;
  /** The resolved bullet character for unordered lists (`-` or `•`). */
  bulletChar?: string;
}

/** Identify the list marker at the start of `line`, if any. */
function parseListMarker(line: string): MarkerInfo | null {
  const numbered = NUMBERED_LINE.exec(line);
  if (numbered) {
    const [marker, indent, numStr] = numbered;
    return {
      marker,
      indent,
      canonical: `${indent}${numStr}. `,
      nextMarker: `${indent}${parseInt(numStr, 10) + 1}. `,
      listType: "ordered",
    };
  }

  const bullet = BULLET_LINE.exec(line);
  if (bullet) {
    const [marker, indent, char] = bullet;
    // Keep `-` as `-`; normalise `*` to the bullet glyph `•`.
    const glyph = char === "-" ? "-" : BULLET_GLYPH;
    const canonical = `${indent}${glyph} `;
    // A bullet list does not count, so the next marker is the same as this one.
    return { marker, indent, canonical, nextMarker: canonical, listType: "unordered", bulletChar: glyph };
  }

  return null;
}

/** Offset of the start of the line containing `caret` (0 when on the first line). */
function lineStartBefore(value: string, caret: number): number {
  return value.lastIndexOf("\n", caret - 1) + 1;
}

/** Offset of the end of the line containing `caret` (exclusive of the newline). */
function lineEndAfter(value: string, caret: number): number {
  const newline = value.indexOf("\n", caret);
  return newline === -1 ? value.length : newline;
}

function indentLengthOf(line: string): number {
  return line.length - line.trimStart().length;
}

/**
 * The number an ordered item should carry at `indentLen`, given the lines above
 * `lineStart`. Walks back to the nearest sibling at the same depth, stopping at a
 * shallower line (the parent) or a blank line (the list block ended) — either of
 * which makes this the first item at its level.
 */
function siblingNumberAt(value: string, lineStart: number, indentLen: number): number {
  let cursor = lineStart;
  while (cursor > 0) {
    const lineEnd = cursor - 1; // the "\n" that ends the previous line
    const prevStart = value.lastIndexOf("\n", lineEnd - 1) + 1;
    const line = value.slice(prevStart, lineEnd);

    if (line.trim() === "") return 1;

    const lineIndentLen = indentLengthOf(line);
    if (lineIndentLen < indentLen) return 1;
    if (lineIndentLen === indentLen) {
      const numbered = NUMBERED_LINE.exec(line);
      return numbered ? parseInt(numbered[2], 10) + 1 : 1;
    }

    cursor = prevStart; // a deeper line — keep walking past this sub-list
  }
  return 1;
}

/** The marker body (no indent) an item of `listType` carries at `indentLen`. */
function markerBodyFor(
  value: string,
  lineStart: number,
  indentLen: number,
  listType: ListType,
  bulletChar: string = BULLET_GLYPH,
): string {
  if (listType === "unordered") return `${bulletChar} `;
  return `${siblingNumberAt(value, lineStart, indentLen)}. `;
}

export function listActivationEdit(
  value: string,
  selectionStart: number,
  selectionEnd: number,
): ListActivationResult | null {
  // With a non-collapsed selection, space should replace the selection as usual.
  if (selectionStart !== selectionEnd) return null;

  const caret = selectionStart;

  // Activation only applies at the end of a line. If there is text after the
  // caret the space is an ordinary edit, and rewriting the line would move text
  // the user did not ask us to touch.
  const nextChar = value.charAt(caret);
  if (nextChar !== "" && nextChar !== "\n") return null;

  const lineStart = lineStartBefore(value, caret);
  const lineUpToCaret = value.slice(lineStart, caret);

  const ordered = ORDERED_ACTIVATION.exec(lineUpToCaret);
  if (ordered) {
    const [, indent, numStr] = ordered;
    // The number the user typed wins — `7.` opens a list at 7, as it did before.
    return activate(value, lineStart, caret, `${indent}${LIST_INDENT}${numStr}. `, "ordered");
  }

  const bullet = BULLET_ACTIVATION.exec(lineUpToCaret);
  if (bullet) {
    const [, indent, char] = bullet;
    // Keep `-` as `-`; convert `*` to the bullet glyph `•`.
    const glyph = char === "-" ? "-" : BULLET_GLYPH;
    return activate(value, lineStart, caret, `${indent}${glyph} `, "unordered");
  }

  return null;
}

/** Replace the marker the user typed with its indented, canonical form. */
function activate(
  value: string,
  lineStart: number,
  caret: number,
  canonical: string,
  listType: ListType,
): ListActivationResult {
  return {
    value: value.slice(0, lineStart) + canonical + value.slice(caret),
    caret: lineStart + canonical.length,
    listType,
  };
}

export function listIndentEdit(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  direction: IndentDirection,
): ListIndentResult | null {
  // A selection spanning lines is a block operation we deliberately do not do;
  // Tab keeps its default behaviour there.
  if (selectionStart !== selectionEnd) return null;

  const caret = selectionStart;
  const lineStart = lineStartBefore(value, caret);
  const lineEnd = lineEndAfter(value, caret);
  const line = value.slice(lineStart, lineEnd);

  // The whole line is parsed, not just the part before the caret, so Tab works
  // with the caret anywhere in the item.
  const info = parseListMarker(line);
  if (!info) return null;

  const indentLen = info.indent.length;
  let newIndentLen: number;
  if (direction === "outdent") {
    // Never outdent past the base level. Ordered lists keep the cosmetic indent
    // (base = LIST_INDENT), bullet lists sit at column 0 (base = 0).
    const baseIndent = info.listType === "unordered" ? 0 : LIST_INDENT.length;
    if (indentLen <= baseIndent) return null;
    newIndentLen = indentLen - LIST_INDENT.length;
  } else {
    newIndentLen = indentLen + LIST_INDENT.length;
  }

  const markerBody = markerBodyFor(value, lineStart, newIndentLen, info.listType, info.bulletChar);
  const newLine = " ".repeat(newIndentLen) + markerBody + line.slice(info.marker.length);
  const newMarkerEnd = lineStart + newIndentLen + markerBody.length;

  return {
    value: value.slice(0, lineStart) + newLine + value.slice(lineEnd),
    // Keep the caret's offset within the item's text; never let it land inside
    // the marker we just rewrote.
    caret: Math.max(newMarkerEnd, caret + (newLine.length - line.length)),
    listType: info.listType,
    direction,
  };
}

export function nextListEdit(
  value: string,
  selectionStart: number,
  selectionEnd: number,
): ListEditResult | null {
  // With a non-collapsed selection, Enter should replace the selection as usual.
  if (selectionStart !== selectionEnd) return null;

  const caret = selectionStart;
  const lineStart = lineStartBefore(value, caret);
  const lineUpToCaret = value.slice(lineStart, caret);

  const info = parseListMarker(lineUpToCaret);
  if (!info) return null;

  const { marker, indent, canonical, nextMarker, listType, bulletChar } = info;
  const afterMarker = lineUpToCaret.slice(marker.length);
  const afterMarkerOnLine = value.slice(lineStart + marker.length);

  if (afterMarker.trim().length === 0) {
    // Empty nested item → step out one level rather than killing the whole list.
    if (indent.length >= LIST_INDENT.length * 2) {
      const newIndentLen = indent.length - LIST_INDENT.length;
      const markerBody = markerBodyFor(value, lineStart, newIndentLen, listType, bulletChar);
      const newLine = " ".repeat(newIndentLen) + markerBody;
      return {
        value: value.slice(0, lineStart) + newLine + afterMarkerOnLine,
        caret: lineStart + newLine.length,
      };
    }
    // Empty item at the base level → terminate the list by stripping the marker.
    return {
      value: value.slice(0, lineStart) + afterMarkerOnLine,
      caret: lineStart,
    };
  }

  const afterCaret = value.slice(caret);
  const insert = `\n${nextMarker}`;

  // Normalise the current line if the user wrote `1.text` / `1 text` / `* text`
  // before appending the next marker.
  if (marker !== canonical) {
    const correctedLine = canonical + afterMarker;
    return {
      value: value.slice(0, lineStart) + correctedLine + insert + afterCaret,
      caret: lineStart + correctedLine.length + insert.length,
    };
  }

  return {
    value: value.slice(0, caret) + insert + afterCaret,
    caret: caret + insert.length,
  };
}

/**
 * Convert the composer's display form to the markdown that ships in the payload:
 * drop the one cosmetic indent level every list line carries, and rewrite the
 * `•` glyph back to a dash. Non-list lines pass through untouched.
 */
export function toMarkdownList(value: string): string {
  return value
    .split("\n")
    .map((line) => {
      const match = MARKDOWN_LIST_LINE.exec(line);
      if (!match) return line;

      const [matched, indent, markerChar, space] = match;
      const outdented = indent.startsWith(LIST_INDENT) ? indent.slice(LIST_INDENT.length) : indent;
      const marker = markerChar;
      return outdented + marker + space + line.slice(matched.length);
    })
    .join("\n");
}
